/**
 * PluginsPanel - Show loaded plugins status
 */

import React from 'react';

interface PluginInfo {
  id: string;
  name: string;
  version: string;
  description: string;
  category?: string;
  source?: string;
  tags?: string[];
}

interface PluginsPanelProps {
  plugins: PluginInfo[];
  stats?: {
    enabled: number;
    total: number;
  };
}

export function PluginsPanel({ plugins, stats }: PluginsPanelProps) {
  const displayStats = stats || {
    enabled: plugins.length,
    total: plugins.length
  };

  return (
    <div className="plugins-panel">
      <div className="panel-header">
        <h2>🔌 Loaded Plugins</h2>
        <div className="stats">
          <span className="stat">
            <strong>{displayStats.enabled}</strong> enabled
          </span>
          <span className="stat">
            <strong>{displayStats.total}</strong> total
          </span>
        </div>
      </div>

      <div className="plugins-grid">
        {plugins.map((plugin) => (
          <div key={plugin.id} className="plugin-card">
            <div className="plugin-header">
              <span className="plugin-icon">🔌</span>
              <div className="plugin-info">
                <h3>{plugin.name}</h3>
                <span className="plugin-version">v{plugin.version}</span>
              </div>
              <span className="plugin-status">✓</span>
            </div>
            <p className="plugin-description">{plugin.description}</p>
            <div className="plugin-meta">
              <span className="plugin-category">{plugin.category}</span>
              <span className="plugin-source">{plugin.source}</span>
            </div>
            {plugin.tags && plugin.tags.length > 0 && (
              <div className="plugin-tags">
                {plugin.tags.map((tag) => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <style>{`
        .plugins-panel {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid var(--border);
        }

        .panel-header h2 {
          margin: 0;
          font-size: 1.75rem;
          color: var(--text);
        }

        .stats {
          display: flex;
          gap: 1.5rem;
        }

        .stat {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .stat strong {
          font-size: 1.25rem;
          color: var(--primary);
          margin-right: 0.25rem;
        }

        .plugins-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .plugin-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 1.5rem;
          transition: all 0.2s;
        }

        .plugin-card:hover {
          border-color: var(--primary);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
          transform: translateY(-2px);
        }

        .plugin-header {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .plugin-icon {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .plugin-info {
          flex: 1;
        }

        .plugin-info h3 {
          margin: 0;
          font-size: 1.125rem;
          color: var(--text);
        }

        .plugin-version {
          font-size: 0.75rem;
          color: var(--text-secondary);
          font-family: monospace;
        }

        .plugin-status {
          color: var(--success, #10b981);
          font-size: 1.25rem;
        }

        .plugin-description {
          color: var(--text-secondary);
          font-size: 0.875rem;
          line-height: 1.5;
          margin: 0 0 1rem 0;
        }

        .plugin-meta {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .plugin-category,
        .plugin-source {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          background: var(--bg);
          color: var(--text-secondary);
          text-transform: capitalize;
        }

        .plugin-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .tag {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          background: var(--primary);
          color: white;
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
}

export default PluginsPanel;
